import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { FreewheelConstants } from 'tag/ts/tag/ads/freewheel/constants/FreewheelConstants';
import { FreewheelParameterType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelParameterType';
import { FreewheelRequestType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelRequestType';
import { IFreewheelRequestArgument } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequestArgument';
import { FreewheelParameter } from 'tag/ts/tag/ads/freewheel/parameters/FreewheelParameter';
import { AbstractFreewheelRequest } from 'tag/ts/tag/ads/freewheel/requests/AbstractFreewheelRequest';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { UserModel } from 'tag/ts/tag/model/vo/UserModel';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Creates and returns Freewheel DAI API request
 */
export class FreewheelDaiApiRequest extends AbstractFreewheelRequest {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'FreewheelDaiApiRequest',
				type: FreewheelRequestType.DAI_API
			} as IFreewheelRequestArgument
		);

		const siteSessionData = this.tag.model.siteUtilities.siteSessionData;
		const paramsVO = this.tag.params;
		const apiMetadata = this.tag?.model?.apiMetadata;
		const constants = FreewheelConstants.getInstance();
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const isLive = !!this.tag.model.contentUtilities.isLive( paramsVO, apiMetadata );
		const isKids = !!this.tag.model.contentUtilities.isKidsContent( apiMetadata );
		const siteSectionId = siteSessionData?.csid?.toLowerCase() || this.createSiteSectionId();
		const userModel = new UserModel( { tag: this.tag, paramsUser: paramsVO.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();
		const sb = currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined;
		const tsb = currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined;
		const tfcd = !this.utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		[
			{ key: 'api-key', value: '7j3n273bfj7jcnvf22d713a51f', type: FreewheelParameterType.GLOBAL },
			{ key: 'correlator', value: '2421602216386519', type: FreewheelParameterType.GLOBAL },
			{ key: 'imafw__fw_ae', value: paramsVO?.USER?.mvpdId, type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_continuous_play', value: ( !!isLive ? undefined : siteSessionData?.cpSession ), type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_coppa', value: siteSessionData?.tfcd?.toString(), type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_did', value: 'unknown:54bc9b53-79ac-4946-98ba-2f989e315418', type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_is_lat', value: '0', type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_vcid2', value: 'b539d5e0d43130d617d64f8991f0a3e0', type: FreewheelParameterType.SITE },
			{ key: 'imafw_cpPre', value: ( !!isLive ? undefined : siteSessionData?.cpPre ), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_cpSession', value: ( !!isLive ? undefined : siteSessionData?.cpSession ), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_csid', value: siteSectionId, type: FreewheelParameterType.GLOBAL },
			{ key: 'imafw_ge', value: ( !isKids ? siteSessionData?.ge : undefined ), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_gr', value: ( !isKids ? siteSessionData?.gr : undefined ), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_host', value: siteSessionData?.host, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_is_lat', value: '0', type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_playername_version', value: 'Avia JS Video Player_1.9.1', type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sb', value: sb, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_session', value: siteSessionData?.session, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sub', value: siteSessionData?.sub, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_subses', value: siteSessionData?.subses, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sz', value: constants.size, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_tfcd', value: tfcd, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_tsb', value: tsb, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_user', value: paramsVO?.CONTEXT?.metadata?.userProfileCategory, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_vguid', value: siteSessionData?.vguid, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_cadence', value: siteSessionData?.billing, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sublife', value: siteSessionData?.sublife, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_packageType', value: siteSessionData?.packageType, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_profile', value: siteSessionData?.profile?.toString(), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_vendorUserType', value: siteSessionData?.vendorUserType, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_station', value: this.tag?.model?.adUtilities?.getCallSignId?.(), type: FreewheelParameterType.CUSTOM }
		].forEach( ( p ) => this.addParameter( new FreewheelParameter( p.key, p.value, p.type ) ) );

		// create FreewheelParameters from FMS and append them
		this.addFmsParameters( fmsResponse );
	}
}